import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../auth/AuthContext.js';
import { useTitle } from '../lib/use-title.js';
import { cn } from '../lib/cn.js';
import { type, button, field, banner } from './theme.js';
import { WaveField } from './WaveField.js';

/**
 * 工厂端登录页(H1b,2026-09-09 按用户定的方向重做)。
 *
 * ── 形态:左深右白,一屏,不滚 ─────────────────────────────────
 *
 * 参考的是顶级 SaaS 的登录页长相:**右边只有一件事 —— 扫码**;左边是品牌、
 * 一句话、两张真截图斜着放、底层一张 three.js 的点阵。整页可见中文不到 60 字。
 *
 * 上一版是「销售页 + 登录卡」:三个价值屏、三档合作方式、四段说明。用户的话是
 * 「像菜市场老太太絮叨」「太土」。这一页是工厂老板对产品的第一眼,要的是有分量,
 * 不是信息完整 —— 要说的话在 link4a.com 官网说。守卫钉着可见字数不许回涨。
 *
 * ── 截图是真的,只露焦点区 ───────────────────────────────────
 *
 * `public/portal-shots/` 那几张是 playwright 从跑起来的工厂端抓的。
 * 这次不再整页缩略(上一版缩到 650 宽,字糊成一团),而是 16:10 的框、
 * 图放到 150% 宽对齐左上,于是会话页露出的是「侧栏 + 聊天列」,字看得清。
 *
 * ── 不变的东西 ───────────────────────────────────────────────
 *
 * 登录逻辑一行没动:挂载即出码、并行取 login-options、轮询、过期换一张、
 * 微信内置浏览器走网页授权、演示登录由后端决定画不画。
 */
export default function PortalLoginPage() {
  useTitle('登录 · 工厂端 · link4a');
  const { status } = useAuth();
  const [params] = useSearchParams();
  const navigate = useNavigate();

  const opts = useQuery({
    queryKey: ['portal-login-options'],
    queryFn: async (): Promise<{ wechat_login: boolean; demo_login?: boolean }> => {
      const res = await fetch('/api/tenant-auth/login-options', { credentials: 'include' });
      if (!res.ok) return { wechat_login: false };
      return (await res.json()) as { wechat_login: boolean; demo_login?: boolean };
    },
    // 读不到按「没有微信」画:少画一个入口只是不方便,画一个点了 404 的按钮是让人以为系统坏了
    retry: false,
  });

  useEffect(() => {
    if (status === 'authenticated') {
      navigate(params.get('next') ?? '/portal', { replace: true });
    }
  }, [status, params, navigate]);

  const wechat = opts.data?.wechat_login === true;
  /**
   * 二维码跟 `login-options` 并行取:手机上二维码就是首屏最大的元素(LCP),
   * 串着取白白多一次往返。没开通微信的租户会多打一次 404,可以接受。
   */
  const qr = useWechatQr();
  /** 演示登录由后端决定画不画(`PORTAL_DEMO_LOGIN=1`),前端没有开关 */
  const demo = opts.data?.demo_login === true;

  return (
    <div
      className="grid min-h-[100dvh] bg-portal-ink font-portal lg:grid-cols-[minmax(0,1.35fr)_minmax(420px,0.85fr)]"
      data-testid="portal-login-page"
    >
      {/* 手机上登录卡在最上(order-first),桌面回到右栏 */}
      <div className="order-first flex min-h-[100dvh] items-center justify-center bg-portal-paper px-8 py-10 lg:order-last lg:min-h-0">
        <LoginPanel wechat={wechat} loading={opts.isLoading} demo={demo} qr={qr} />
      </div>

      <Showcase />
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   左栏:品牌 / 一句话 / 两张斜放的截图 / 点阵
   ══════════════════════════════════════════════════════════════ */

const SHOT_FRONT = '/portal-shots/value-conversation.png';
const SHOT_BACK = '/portal-shots/value-orders.png';

function Showcase(): JSX.Element {
  const shots = useRef<HTMLDivElement>(null);

  /**
   * 鼠标移动时两张图各转 ±3°。只在桌面、且用户没有要求减少动效时挂监听;
   * 直接改 style 而不走 state:mousemove 一秒几十次,走 React 重渲染是浪费。
   */
  useEffect(() => {
    const host = shots.current;
    if (!host || typeof window === 'undefined') return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    if (!window.matchMedia('(min-width: 1024px)').matches) return;
    const front = host.querySelector<HTMLElement>('[data-shot="front"]');
    const back = host.querySelector<HTMLElement>('[data-shot="back"]');
    if (!front || !back) return;
    const onMove = (e: MouseEvent): void => {
      const x = e.clientX / window.innerWidth - 0.5;
      const y = e.clientY / window.innerHeight - 0.5;
      front.style.transform = `rotateY(${14 + x * 6}deg) rotateX(${6 - y * 5}deg) rotateZ(-1.5deg)`;
      back.style.transform = `rotateY(${-10 + x * 6}deg) rotateX(${5 - y * 5}deg) translateZ(-120px)`;
    };
    window.addEventListener('mousemove', onMove, { passive: true });
    return () => window.removeEventListener('mousemove', onMove);
  }, []);

  return (
    <section className="relative flex min-w-0 flex-col justify-between overflow-hidden px-7 py-8 lg:min-h-[100dvh] lg:px-20 lg:py-14">
      <WaveField className="absolute inset-0 z-0 hidden h-full w-full lg:block" />

      <a
        href="https://link4a.com"
        className="relative z-10 flex items-center gap-3 text-portal-paper"
      >
        <span
          className="relative h-[30px] w-[30px] shrink-0 rounded-portal-md bg-gradient-to-br from-portal-accent to-portal-ink shadow-[inset_0_0_0_1px_rgb(255_255_255/0.16)]"
          aria-hidden
        >
          <span className="absolute left-2 top-2 h-2 w-2 rounded-full bg-portal-paper" />
          <span className="absolute bottom-[7px] right-[7px] h-1.5 w-1.5 rounded-full bg-portal-glow" />
        </span>
        <span className="text-portal-body font-bold tracking-wide">链上云帆</span>
        <span className="text-portal-meta font-semibold uppercase tracking-[0.22em] text-portal-mute">
          link4a
        </span>
      </a>

      <div className="relative z-10 my-auto pt-[6vh]">
        <h1 className="text-portal-display font-bold tracking-tight text-portal-paper [text-wrap:balance]">
          工厂自己的外贸客户端
        </h1>
        <p className="mt-4 text-portal-lg text-portal-mist">询盘、客户、订单进度，一个地方。</p>

        {/*
          透视舞台。桌面上高度按视口给、允许向右溢出 12vw(第二张被右栏切掉一截是刻意的出血);
          手机上只留第一张,不斜、100% 宽。
        */}
        <div
          ref={shots}
          className="relative mt-[3vh] lg:-mr-[12vw] lg:h-[min(56vh,560px)] lg:[perspective:1800px] lg:[perspective-origin:70%_40%]"
          data-testid="portal-login-shots"
        >
          <Shot
            src={SHOT_FRONT}
            alt="会话页"
            which="front"
            className="relative w-full lg:absolute lg:left-0 lg:top-0 lg:z-20 lg:w-[min(78%,900px)] lg:[transform:rotateY(14deg)_rotateX(6deg)_rotateZ(-1.5deg)]"
          />
          <Shot
            src={SHOT_BACK}
            alt="订单页"
            which="back"
            className="hidden lg:absolute lg:left-[60%] lg:top-[26%] lg:z-10 lg:block lg:w-[min(54%,640px)] lg:opacity-80 lg:[transform:rotateY(-10deg)_rotateX(5deg)_translateZ(-120px)]"
          />
        </div>
      </div>

      <p className="relative z-10 mt-10 flex items-center gap-4 text-portal-meta text-portal-mute lg:mt-0">
        <span>© 链上云帆</span>
        <a href="https://link4a.com" className="hover:text-portal-mist">
          link4a.com
        </a>
      </p>
    </section>
  );
}

/**
 * 一张截图:16:10 的框,图放到 150% 宽、对齐左上 —— 只露焦点区,字看得清。
 * 圆角 + 重投影 + 1px 白描边 + 蓝泛光在 `shadow-portal-shot` 里;表面一道斜向高光。
 */
function Shot({
  src,
  alt,
  which,
  className,
}: {
  src: string;
  alt: string;
  which: 'front' | 'back';
  className?: string;
}): JSX.Element {
  return (
    <figure
      data-shot={which}
      className={cn(
        'm-0 aspect-[16/10] overflow-hidden rounded-portal-lg bg-portal-paper shadow-portal-shot [transform-style:preserve-3d] [will-change:transform]',
        "after:pointer-events-none after:absolute after:inset-0 after:bg-[linear-gradient(110deg,transparent_40%,rgb(255_255_255/0.08)_60%,transparent_70%)] after:content-['']",
        className,
      )}
    >
      <img
        src={src}
        alt={alt}
        loading="eager"
        decoding="async"
        className="block h-auto w-[150%] max-w-none origin-top-left"
      />
    </figure>
  );
}

/* ══════════════════════════════════════════════════════════════
   右栏:登录。只有这几样,多一个字都不要
   ══════════════════════════════════════════════════════════════ */

function LoginPanel({
  wechat,
  loading,
  demo,
  qr,
}: {
  wechat: boolean;
  loading: boolean;
  demo: boolean;
  qr: WechatQr;
}): JSX.Element {
  return (
    <section
      className="w-full max-w-[380px] text-center"
      aria-label="登录"
      data-testid="portal-login-panel"
    >
      <h2 className="text-portal-h1 font-bold tracking-tight text-portal-ink">登录</h2>
      <p className={cn(type.caption, 'mt-1.5')}>微信扫一扫</p>

      {/*
        三种状态(还在问后端 / 有微信 / 没微信)共用一个固定高的槽,
        骨架和真码同尺寸,出码前后不跳版(CLS)。
      */}
      <div className="mx-auto mt-7 min-h-[248px]">
        {loading ? (
          <div
            className="mx-auto h-[232px] w-[232px] animate-pulse rounded-portal-xl bg-portal-tint"
            data-testid="portal-login-loading"
          />
        ) : wechat ? (
          inWechat() ? (
            /* 微信内置浏览器里才走网页授权:`oauth2/authorize` 在 PC 浏览器里点它必挂 */
            <a
              href="/api/wechat/oauth/start"
              data-testid="portal-wechat-login-btn"
              className={cn(
                button.primary,
                button.block,
                'bg-portal-wechat hover:bg-portal-wechat-hover',
              )}
            >
              用微信登录
            </a>
          ) : (
            <WechatQrLogin qr={qr} />
          )
        ) : (
          <p className={cn(banner.neutral, 'text-left')} data-testid="portal-wechat-unavailable">
            微信登录开通中，请用业务员发给你的登录链接进入。
          </p>
        )}
      </div>

      {wechat && (
        <p className={cn(type.caption, 'mt-8')}>
          没有微信？
          {/*
            落点是官网留资那一段里的那句「已经在合作？登录工厂端。没有登录方式，
            找对接你的业务员要一条链接」。以前指的是 link4a.com 首页 —— 而首页当时
            没有任何登录入口，工厂点过去转一圈又回到原地。#login-help 这个锚点
            由 apps/landing 那边的守卫钉着，别改成裸域名。
          */}
          <a href="https://link4a.com/#login-help" className="font-medium text-portal-accent">
            向对接你的业务员要登录链接
          </a>
        </p>
      )}

      <p className={cn(type.meta, 'mt-14')}>登录即表示你同意服务条款</p>

      {demo && <DemoLogin />}
    </section>
  );
}

/** 是不是在微信里打开的。放在函数里是为了用例能连 UA 一起换掉 */
function inWechat(): boolean {
  return /MicroMessenger/i.test(navigator.userAgent ?? '');
}

type QrState =
  | { phase: 'loading' }
  | { phase: 'ready'; ticket: string; qrUrl: string; pollMs: number }
  | { phase: 'expired' }
  | { phase: 'error'; message: string };

/**
 * PC 扫码登录(W1)。
 *
 * 页面出一张带参二维码 → 工厂用微信扫(顺带关注服务号)→ 微信把事件推给我们 →
 * 后端认出这个 openid 是谁 → 这边轮询到 `authorized` 就已经有 session 了。
 *
 * 轮询不用长连接:一次登录最多转几十次。停止条件写死三个(成功 / 待认领 / 过期),
 * 没有「一直转下去」的分支。挂载就出码;过期之后要人点「换一张」——
 * 那时候人多半已经走开了,自动续码等于让一台没人看的电脑一直要凭据。
 */
export interface WechatQr {
  state: QrState;
  restart: () => void;
}

function useWechatQr(): WechatQr {
  const [state, setState] = useState<QrState>({ phase: 'loading' });
  // StrictMode 下 effect 跑两次,不挡的话首屏会连出两张码(后一张把前一张作废)
  const started = useRef(false);

  const start = async (): Promise<void> => {
    setState({ phase: 'loading' });
    try {
      const res = await fetch('/api/wechat/qrcode', { method: 'POST', credentials: 'include' });
      if (!res.ok) {
        setState({ phase: 'error', message: '微信登录暂时不可用，请用业务员发的登录链接' });
        return;
      }
      const body = (await res.json()) as {
        ticket?: string;
        qr_url?: string;
        poll_interval_ms?: number;
      };
      // 没有 ticket 就不进 ready:微信没开通时某些反代会回一个 200 的 JSON,进了 ready 会拿着 undefined 一直轮询
      if (!body.ticket || !body.qr_url) {
        setState({ phase: 'error', message: '微信登录暂时不可用，请用业务员发的登录链接' });
        return;
      }
      setState({
        phase: 'ready',
        ticket: body.ticket,
        qrUrl: body.qr_url,
        pollMs: body.poll_interval_ms || 2000,
      });
    } catch {
      setState({ phase: 'error', message: '网络不通，检查一下网络再试' });
    }
  };

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    void start();
  }, []);

  useEffect(() => {
    if (state.phase !== 'ready') return;
    let stopped = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const tick = async (): Promise<void> => {
      try {
        const res = await fetch(`/api/wechat/qrcode/${encodeURIComponent(state.ticket)}`, {
          credentials: 'include',
        });
        const body = (await res.json().catch(() => null)) as {
          status?: string;
          redirect?: string;
        } | null;
        if (stopped) return;
        // 401 = 过期 / 已用过:两种都只能重新出码
        if (!res.ok || body?.status === 'expired' || body?.status === 'consumed') {
          setState({ phase: 'expired' });
          return;
        }
        if (body?.status === 'authorized' || body?.status === 'unbound') {
          // 整页跳转不是 SPA 路由:AuthContext 在启动时读一次 /api/me,前端跳的话拿的还是「未登录」
          window.location.href = body.redirect ?? '/portal';
          return;
        }
      } catch {
        // 单次轮询失败不算失败(切了下网、页面在后台):下一拍继续
      }
      if (!stopped) timer = setTimeout(() => void tick(), state.pollMs);
    };

    timer = setTimeout(() => void tick(), state.pollMs);
    return () => {
      stopped = true;
      if (timer) clearTimeout(timer);
    };
  }, [state]);

  return { state, restart: () => void start() };
}

/**
 * 二维码框 232 见方,浅灰底 + 发丝线,下沿一枚「● 微信」小胶囊。
 * 三种状态共用同一个框:出码前后不跳版。加载态不画转圈(转圈让人以为要等很久)。
 */
function WechatQrLogin({ qr }: { qr: WechatQr }): JSX.Element {
  const { state, restart } = qr;
  return (
    <div className="relative mx-auto w-[232px]" data-testid="portal-wechat-qr">
      <div className="flex h-[232px] w-[232px] items-center justify-center rounded-portal-xl border border-portal-line bg-portal-tint">
        {state.phase === 'ready' ? (
          <img
            src={state.qrUrl}
            alt="微信登录二维码"
            width={196}
            height={196}
            className="h-[196px] w-[196px] rounded-portal-sm"
            data-testid="portal-wechat-qr-img"
          />
        ) : state.phase === 'expired' ? (
          <div className="text-center" data-testid="portal-wechat-qr-expired">
            <p className={type.caption}>二维码过期了</p>
            <button
              onClick={restart}
              className={cn(button.secondary, button.compact, 'mt-2')}
              data-testid="portal-wechat-qr-refresh"
            >
              换一张
            </button>
          </div>
        ) : state.phase === 'error' ? (
          <p className={cn(type.caption, 'px-4 text-center')} data-testid="portal-wechat-error">
            {state.message}
          </p>
        ) : (
          <div className="h-[196px] w-[196px] animate-pulse rounded-portal-sm bg-portal-tint2" />
        )}
      </div>
      <span className="absolute -bottom-3 left-1/2 flex -translate-x-1/2 items-center gap-1.5 whitespace-nowrap rounded-full border border-portal-line bg-portal-paper px-2.5 py-0.5 text-portal-meta text-portal-char">
        <i className="inline-block h-2 w-2 rounded-full bg-portal-wechat" aria-hidden />
        微信
      </span>
    </div>
  );
}

/**
 * 演示登录:输一个档位,直接以演示租户的身份进来(`PORTAL_DEMO_LOGIN=1` 才画)。
 * 进的是 `demo-free` / `demo-pro` 两个专门建的演示租户,跟真客户的数据没有关系。
 */
function DemoLogin(): JSX.Element {
  const [tier, setTier] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const go = async (): Promise<void> => {
    setBusy(true);
    setErr(null);
    try {
      const res = await fetch('/api/tenant-auth/demo-login', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ tier: tier.trim() }),
      });
      const body = (await res.json().catch(() => null)) as { message?: string } | null;
      if (!res.ok) {
        setErr(body?.message ?? '进不去，看一眼服务端日志');
        return;
      }
      // 整页刷新而不是 navigate:AuthContext 是启动时读一次 /api/me
      window.location.href = '/portal';
    } catch (e) {
      setErr(e instanceof Error ? e.message : '网络错误');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className={cn(banner.warn, 'mt-8 flex-col items-stretch text-left')}
      data-testid="portal-demo-login"
    >
      <div className="font-medium">演示登录（临时）</div>
      <p className="mt-0.5">
        输 <b>0</b> 看免费档，<b>1</b> 看 Pro 档。用的是演示租户，不是真客户数据。
      </p>
      <div className="mt-2 flex gap-2">
        <input
          value={tier}
          onChange={(e) => setTier(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') void go();
          }}
          placeholder="0 或 1"
          inputMode="numeric"
          aria-label="演示档位"
          className={cn(field.input, 'w-24 py-1.5')}
          data-testid="portal-demo-tier"
        />
        <button
          onClick={() => void go()}
          disabled={busy || !tier.trim()}
          className={cn(button.secondary, button.compact)}
          data-testid="portal-demo-go"
        >
          {busy ? '进入中…' : '进入'}
        </button>
      </div>
      {err && (
        <p className="mt-2" data-testid="portal-demo-error">
          {err}
        </p>
      )}
    </div>
  );
}
